import icn_linebar_chart from "assets/images/icons/icn_linebar_chart.svg";

import React, { useMemo, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import WirelessChannelInterfaceSliderChart from "pages/AInsight/LinkIntegrity/charts/WirelessChannelInterfaceSliderChart";

const DEFAULT_DISPLAY_COUNT = 2;

const BottomActionBar = ({
  parentId,
  show,
  onCheckFormValid,
  onCancel,
  onApply,
}) => {
  useEffect(() => {
    const parentElement = document.getElementById(parentId);
    const bottomActionBarElement = document.getElementById("bottomActionBar");

    if (!parentElement || !bottomActionBarElement) return;

    const updateWidth = () => {
      bottomActionBarElement.style.width = `${parentElement.clientWidth - 32}px`;
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(parentElement);
    updateWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, [parentId]);

  return (
    <Box
      id="bottomActionBar"
      sx={{
        position: "fixed",
        bottom: "20px",
        width: 0,
        display: show ? "flex" : "none",
        justifyContent: "center",
        gap: 2,
        margin: "16px 0",
        padding: "16px",
        backgroundColor: "primary.lightBlue",
        transform: show ? "translateY(0)" : "translateY(80px)",
        transition: "transform 0.3s ease-in-out",
        zIndex: 1300,
      }}
    >
      <OutlineButton
        onClick={onCancel}
        sx={{ marginRight: "20px" }}
        width="100px"
        height="32px"
      >
        Cancel
      </OutlineButton>

      <SolidButton
        onClick={onApply}
        width="100px"
        height="32px"
        disabled={!onCheckFormValid()}
      >
        Apply
      </SolidButton>
    </Box>
  );
};

const WirelessChannelInterfaceDrawer = ({
  wirelessChannelInterfaceChartSeriesData,
  wirelessChannelInterfaceApiChartData,
  wirelessChannelInterfaceApList,
  auth,
  onRefreshWirelessChannelInterface,
}) => {
  const theme = useTheme();
  const { sendRequest } = useDataSourceManager();
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const apiThreshold = Number(
    wirelessChannelInterfaceApiChartData?.threshold ?? -85,
  );

  const [threshold, setThreshold] = useState(apiThreshold);
  const [originalThreshold, setOriginalThreshold] = useState(apiThreshold);
  const [showBottomBar, setShowBottomBar] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const status =
    wirelessChannelInterfaceApiChartData.avgRssi > -60
      ? "Bad"
      : wirelessChannelInterfaceApiChartData.avgRssi <= -70 &&
        wirelessChannelInterfaceApiChartData.avgRssi <= -60
        ? "Warning"
        : "Good";

  const statusColorMap = {
    Good: theme.palette.primary.greenPrimary,
    Warning: theme.palette.primary.orangeThird,
    Bad: theme.palette.primary.redPrimary,
  };

  const apList = useMemo(
    () =>
      (wirelessChannelInterfaceApList?.apList ?? []).filter(
        ({ rssi }) => Number(rssi) > -60,
      ),
    [wirelessChannelInterfaceApList?.apList],
  );

  const displayApList = useMemo(() => {
    if (showAll) return apList;

    return apList.slice(0, DEFAULT_DISPLAY_COUNT);
  }, [apList, showAll]);

  useEffect(() => {
    const nextThreshold = Number(
      wirelessChannelInterfaceApiChartData?.threshold ?? -85,
    );

    setThreshold(nextThreshold);
    setOriginalThreshold(nextThreshold);
    setShowBottomBar(false);
  }, [wirelessChannelInterfaceApiChartData?.threshold]);

  useEffect(() => {
    setShowAll(false);
  }, [wirelessChannelInterfaceApList]);

  const wirelessChannelInterfaceColumns = useMemo(
    () => [
      columnHelper.accessor("ssidName", {
        header: "SSID Name",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("bssid", {
        header: "BSSID",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("channel", {
        header: "Channel",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("rssi", {
        header: "Level (dBm)",
        cell: (info) =>
          info.getValue() === undefined || info.getValue() === null
            ? "-"
            : `${info.getValue()} dBm`,
      }),
    ],
    [columnHelper],
  );

  const wirelessChannelInterfaceTable = useTableInstance({
    tableName: "wirelessChannelInterfaceDrawerTable",
    columns: wirelessChannelInterfaceColumns,
    data: displayApList,
    enableSorting: false,
    enablePagination: false,
  });

  const handleThresholdChange = ({ value }) => {
    setThreshold(value);
    setShowBottomBar(value !== originalThreshold);
  };

  const handleCancel = () => {
    setThreshold(originalThreshold);
    setShowBottomBar(false);
  };

  const handleApply = async () => {
    try {
      const apiPath = auth?.orgs?.id
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/linkIntegrity/channelInterference/threshold.update"
        : "/orgs/:orgId/sites/:siteId/linkIntegrity/channelInterference/threshold.update";

      const restfulParams = auth?.orgs?.id
        ? {
          entId: auth?.realm?.id,
          orgId: auth?.orgs?.id,
          siteId: auth?.sites?.id,
        }
        : {
          orgId: auth?.realm?.id,
          siteId: auth?.sites?.id,
        };

      const requestParams = {
        data: {
          threshold,
        },
      };

      await sendRequest(apiPath, uuidv4(), restfulParams, requestParams);

      // 這裡要把新的 threshold 傳回外層
      // 外層會先同步更新卡片 status / threshold，再重新 read API
      onRefreshWirelessChannelInterface?.(threshold);

      setOriginalThreshold(threshold);
      setShowBottomBar(false);
    } catch (error) {
      console.error("Failed to update channel interference threshold:", error);
    }
  };

  const shouldShowMoreButton = apList.length > DEFAULT_DISPLAY_COUNT;

  return (
    <Box
      id="wirelessChannelInterfaceDrawer"
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: showBottomBar ? 12 : 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_linebar_chart}
          sx={{ width: 20, height: 20, mr: 1.5 }}
        />

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
          }}
        >
          Wireless Channel Interference
        </Typography>
      </Box>

      <CollapseCard title="Wireless Channel Interference">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Box sx={{ width: "100%" }}>
            <WirelessChannelInterfaceSliderChart
              data={wirelessChannelInterfaceChartSeriesData}
              defaultThreshold={apiThreshold}
              externalThreshold={threshold}
              onThresholdChange={handleThresholdChange}
            />
          </Box>

          <Box>
            <Box
              sx={{
                pb: 2,
                borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.main,
                }}
              >
                High Interference SSIDs ( &gt;-60dBm)
              </Typography>
            </Box>

            <TableField {...wirelessChannelInterfaceTable} />

            {shouldShowMoreButton && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography
                  onClick={() => setShowAll((prev) => !prev)}
                  variant="text"
                  sx={{
                    minWidth: 0,
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {showAll ? "Show Less" : "More..."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CollapseCard>

      <BottomActionBar
        parentId="wirelessChannelInterfaceDrawer"
        show={showBottomBar}
        onCheckFormValid={() => threshold !== originalThreshold}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </Box>
  );
};

export default WirelessChannelInterfaceDrawer;
